import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import { formatMoney } from "../../utils/status";
import {
	getDwollaBalance,
	getVerifiedBankList,
	addBuyerBank,
	deleteBankAccount,
	getTransferList,
	transferAmount,
} from "../../api/funds";

const TABS = ["Overview", "Bank Accounts", "Transactions"];

export default function Funds() {
	const [tab, setTab] = useState("Overview");
	return (
		<div>
			<PageHeader title="Funds" subtitle="Manage your Dwolla balance, bank accounts and transfers" />
			<ul className="nav nav-tabs mb-3">
				{TABS.map((t) => (
					<li className="nav-item" key={t}>
						<button className={`nav-link ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}</button>
					</li>
				))}
			</ul>
			{tab === "Overview" && <Overview />}
			{tab === "Bank Accounts" && <BankAccounts />}
			{tab === "Transactions" && <Transactions />}
		</div>
	);
}

function Overview() {
	const [loading, setLoading] = useState(true);
	const [balance, setBalance] = useState(null);
	const [showTransfer, setShowTransfer] = useState(false);

	const load = async () => {
		setLoading(true);
		const res = await getDwollaBalance();
		if (res && !res.error) setBalance(res.data);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, []);

	if (loading) return <Loader />;

	return (
		<div className="row g-3">
			<div className="col-md-5">
				<div className="card p-4">
					<div className="text-muted small">Available Balance</div>
					<div className="fs-2 fw-bold mb-3">{formatMoney(balance ?? 0)}</div>
					<button className="btn-accent-solid" onClick={() => setShowTransfer(true)}>
						Transfer Funds
					</button>
				</div>
			</div>
			{showTransfer && <TransferModal onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); load(); }} />}
		</div>
	);
}

function TransferModal({ onClose, onDone }) {
	const [amount, setAmount] = useState("");
	const [fromId, setFromId] = useState("");
	const [toId, setToId] = useState("");
	const [accounts, setAccounts] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	// Mirrors ordr-flutter MoneyTransferBloc: both "from" and "to" pickers are
	// populated from the same verified funding-source list (bank accounts + the
	// Dwolla balance itself, distinguished by `type`).
	useEffect(() => {
		(async () => {
			const res = await getVerifiedBankList();
			if (res && !res.error) setAccounts(res.data?.bankList || []);
		})();
	}, []);

	const submit = async () => {
		const fromAccount = accounts.find((a) => a.id === fromId);
		const toAccount = accounts.find((a) => a.id === toId);
		if (!amount || !fromAccount || !toAccount) {
			toast.error("Enter amount and select both accounts");
			return;
		}
		if (fromId === toId) {
			toast.error("From and To accounts must be different");
			return;
		}
		setSubmitting(true);
		const res = await transferAmount({
			sourceId: fromAccount.id,
			destinationId: toAccount.id,
			source: fromAccount.name,
			destination: toAccount.name,
			amount,
			...(fromAccount.type === "balance" ? { withdraw: true } : {}),
		});
		setSubmitting(false);
		if (res && !res.error) {
			toast.success("Transfer initiated");
			onDone();
		} else {
			toast.error(res?.msg || "Could not initiate transfer");
		}
	};

	return (
		<div className="modal d-block" style={{ background: "rgba(0,0,0,0.4)" }}>
			<div className="modal-dialog modal-dialog-centered">
				<div className="modal-content p-4">
					<h6 className="fw-bold mb-3">Transfer Funds</h6>
					<div className="mb-3">
						<label className="form-label">From</label>
						<select className="form-select" value={fromId} onChange={(e) => setFromId(e.target.value)}>
							<option value="">Select account</option>
							{accounts.map((a) => (
								<option key={a.id} value={a.id}>{a.name}</option>
							))}
						</select>
					</div>
					<div className="mb-3">
						<label className="form-label">To</label>
						<select className="form-select" value={toId} onChange={(e) => setToId(e.target.value)}>
							<option value="">Select account</option>
							{accounts.map((a) => (
								<option key={a.id} value={a.id}>{a.name}</option>
							))}
						</select>
					</div>
					<div className="mb-3">
						<label className="form-label">Amount ($)</label>
						<input className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} />
					</div>
					<div className="d-flex gap-2">
						<button className="btn-accent-solid" disabled={submitting} onClick={submit}>
							{submitting ? "Processing..." : "Confirm Transfer"}
						</button>
						<button className="btn-outline" onClick={onClose}>Cancel</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function BankAccounts() {
	const [loading, setLoading] = useState(true);
	const [banks, setBanks] = useState([]);
	const [showAdd, setShowAdd] = useState(false);

	const load = async () => {
		setLoading(true);
		const res = await getVerifiedBankList();
		if (res && !res.error) setBanks((res.data?.bankList || []).filter((b) => b.type === "bank"));
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, []);

	const handleDelete = async (fundingSourceUrl) => {
		if (!window.confirm("Remove this bank account?")) return;
		const res = await deleteBankAccount({ fundingSourceUrl });
		if (res && !res.error) {
			toast.success("Bank account removed");
			load();
		} else {
			toast.error(res?.msg || "Could not remove bank account");
		}
	};

	if (loading) return <Loader />;

	return (
		<div>
			<div className="d-flex justify-content-end mb-3">
				<button className="btn-accent-solid" onClick={() => setShowAdd(true)}>+ Add Bank Account</button>
			</div>
			{banks.length === 0 ? (
				<EmptyState title="No bank accounts linked" subtitle="Link a bank account to fund transfers and receive payments." />
			) : (
				<div className="card">
					<table className="table align-middle mb-0">
						<thead>
							<tr className="text-muted small">
								<th className="ps-3">Bank</th>
								<th>Account (last 4)</th>
								<th>Status</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{banks.map((b) => (
								<tr key={b.id || b._id}>
									<td className="ps-3">{b.name || b.bankName}</td>
									<td>{b.accountNumber ? `••••${String(b.accountNumber).slice(-4)}` : "-"}</td>
									<td><span className="status-pill success">{b.status || "verified"}</span></td>
									<td>
										<button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(b._links?.self?.href)}>Remove</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{showAdd && <AddBankModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
		</div>
	);
}

function AddBankModal({ onClose, onDone }) {
	const [form, setForm] = useState({ bankName: "", accountNumber: "", routing: "" });
	const [submitting, setSubmitting] = useState(false);

	const submit = async () => {
		if (!form.bankName || !form.accountNumber || !form.routing) {
			toast.error("Fill in all fields");
			return;
		}
		setSubmitting(true);
		const res = await addBuyerBank(form);
		setSubmitting(false);
		if (res && !res.error) {
			toast.success("Bank account added");
			onDone();
		} else {
			toast.error(res?.msg || "Could not add bank account");
		}
	};

	return (
		<div className="modal d-block" style={{ background: "rgba(0,0,0,0.4)" }}>
			<div className="modal-dialog modal-dialog-centered">
				<div className="modal-content p-4">
					<h6 className="fw-bold mb-3">Add Bank Account</h6>
					<div className="text-muted small mb-3">
						Instant verification via Plaid is available in the Ordr mobile app; here you can add an account manually for micro-deposit verification.
					</div>
					<div className="mb-3">
						<label className="form-label">Bank name</label>
						<input className="form-control" value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
					</div>
					<div className="mb-3">
						<label className="form-label">Account number</label>
						<input className="form-control" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} />
					</div>
					<div className="mb-3">
						<label className="form-label">Routing number</label>
						<input className="form-control" value={form.routing} onChange={(e) => setForm({ ...form, routing: e.target.value })} />
					</div>
					<div className="d-flex gap-2">
						<button className="btn-accent-solid" disabled={submitting} onClick={submit}>
							{submitting ? "Saving..." : "Add Account"}
						</button>
						<button className="btn-outline" onClick={onClose}>Cancel</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function Transactions() {
	const [loading, setLoading] = useState(true);
	const [transfers, setTransfers] = useState([]);

	useEffect(() => {
		(async () => {
			setLoading(true);
			const res = await getTransferList();
			if (res && !res.error) setTransfers(res.data || []);
			setLoading(false);
		})();
	}, []);

	if (loading) return <Loader />;
	if (transfers.length === 0) return <EmptyState title="No transactions yet" />;

	return (
		<div className="card">
			<table className="table align-middle mb-0">
				<thead>
					<tr className="text-muted small">
						<th className="ps-3">Date</th>
						<th>Description</th>
						<th>Amount</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					{transfers.map((t, idx) => (
						<tr key={t.id || t._id || idx}>
							<td className="ps-3">{t.created ? moment(t.created).format("MMM D, YYYY") : "-"}</td>
							<td>{t.description || t.destinationName || "Transfer"}</td>
							<td>${t.amount?.value ?? t.amount ?? "-"}</td>
							<td><span className="status-pill">{t.status || "-"}</span></td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
